<template>
    <div>

        <b-card>
            <b-card-title class="mb-1">Tipos de entrega</b-card-title>
            <b-card-text class="mb-0">
                Estos son los tipos de entrega que ve el cliente en el checkout. No se crean ni se
                eliminan desde el panel: solo se ajustan sus costos.
            </b-card-text>
        </b-card>

        <b-card title="Envío gratis">
            <b-alert variant="warning" show class="mb-1">
                <div class="alert-body">
                    Aplica a <strong>todos los métodos de entrega</strong>, incluido el recojo en
                    almacén: si el pedido llega a este monto, el cliente no paga envío ni recojo.
                </div>
            </b-alert>

            <b-form-group
                label="Envío gratis desde"
                label-for="free_delivery_threshold"
                description="Debe ser mayor a 0. Con 0 todos los envíos saldrían gratis."
            >
                <b-input-group prepend="S/.">
                    <b-form-input
                        id="free_delivery_threshold"
                        v-model="freeDeliveryThreshold"
                        type="number"
                        min="1"
                        step="1"
                    />
                </b-input-group>
            </b-form-group>

            <b-button
                v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                variant="primary"
                :disabled="savingThreshold"
                @click="saveThreshold"
            >
                <b-spinner v-if="savingThreshold" small class="mr-50" />
                GUARDAR
            </b-button>
        </b-card>

        <div v-if="loading" class="text-center my-3">
            <b-spinner variant="primary" />
        </div>

        <b-row v-else>
            <b-col
                v-for="deliveryType in deliveryTypes"
                :key="deliveryType._id"
                md="6"
            >
                <b-card :title="deliveryType.name">

                    <!-- Costo por zona (DELIVERY) -->
                    <template v-if="deliveryType.locations && deliveryType.locations.length">
                        <b-form-group
                            v-for="location in deliveryType.locations"
                            :key="location.name"
                            :label="`Costo en ${location.name}`"
                            :label-for="`${deliveryType._id}-${location.name}`"
                        >
                            <b-input-group prepend="S/.">
                                <b-form-input
                                    :id="`${deliveryType._id}-${location.name}`"
                                    v-model="location.amount"
                                    type="number"
                                    min="0"
                                    step="0.10"
                                />
                            </b-input-group>
                        </b-form-group>
                    </template>

                    <!-- Costo plano (RECOJO EN ALMACÉN) -->
                    <b-form-group
                        v-else
                        label="Costo general"
                        :label-for="`${deliveryType._id}-amount`"
                        description="Se cobra cuando el almacén elegido no define un costo de recojo propio."
                    >
                        <b-input-group prepend="S/.">
                            <b-form-input
                                :id="`${deliveryType._id}-amount`"
                                v-model="deliveryType.amount"
                                type="number"
                                min="0"
                                step="0.10"
                            />
                        </b-input-group>
                    </b-form-group>

                    <b-button
                        v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                        variant="primary"
                        :disabled="saving === deliveryType._id"
                        @click="save(deliveryType)"
                    >
                        <b-spinner v-if="saving === deliveryType._id" small class="mr-50" />
                        GUARDAR
                    </b-button>

                </b-card>
            </b-col>
        </b-row>

    </div>
</template>

<script>

    import {
        BRow, BCol, BCard, BCardTitle, BCardText, BFormGroup, BFormInput,
        BInputGroup, BButton, BSpinner, BAlert
    } from 'bootstrap-vue';
    import Ripple from 'vue-ripple-directive';
    import axios from '@axios';
    import ToastificationContent from '@core/components/toastification/ToastificationContent.vue';

    export default {
        components: {
            BRow,
            BCol,
            BCard,
            BCardTitle,
            BCardText,
            BFormGroup,
            BFormInput,
            BInputGroup,
            BButton,
            BSpinner,
            BAlert
        },
        directives: {
            Ripple
        },
        data() {
            return {
                deliveryTypes: [],
                freeDeliveryThreshold: null,
                loading: false,
                saving: null,
                savingThreshold: false
            }
        },
        created() {
            this.fetchDeliveryTypes();
        },
        methods: {
            async fetchDeliveryTypes() {
                this.loading = true;

                try {
                    const { data } = await axios.get('/delivery-types');
                    this.deliveryTypes = data.delivery_types;
                    this.freeDeliveryThreshold = data.free_delivery_threshold;
                } catch (error) {
                    this.notify('Error al obtener los tipos de entrega', 'AlertTriangleIcon', 'danger');
                } finally {
                    this.loading = false;
                }
            },
            async saveThreshold() {
                this.savingThreshold = true;

                try {
                    const { data } = await axios.put('/shipping-settings/free-delivery-threshold', {
                        free_delivery_threshold: this.freeDeliveryThreshold
                    });

                    this.freeDeliveryThreshold = data.free_delivery_threshold;
                    this.notify(data.message, 'CheckIcon', 'success');
                } catch (error) {
                    const message = error.response && error.response.data ?
                        error.response.data.message : 'Error al actualizar el monto';

                    this.notify(message, 'AlertTriangleIcon', 'danger');
                    this.fetchDeliveryTypes();
                } finally {
                    this.savingThreshold = false;
                }
            },
            async save(deliveryType) {
                this.saving = deliveryType._id;

                try {
                    const { data } = await axios.put(`/delivery-type/${deliveryType._id}/prices`, {
                        amount: deliveryType.amount,
                        locations: deliveryType.locations
                    });

                    this.notify(data.message, 'CheckIcon', 'success');
                } catch (error) {
                    const message = error.response && error.response.data ?
                        error.response.data.message : 'Error al actualizar los costos';

                    this.notify(message, 'AlertTriangleIcon', 'danger');

                    /* Se recargan los valores guardados para no dejar en pantalla
                       un importe que la base rechazó. */
                    this.fetchDeliveryTypes();
                } finally {
                    this.saving = null;
                }
            },
            notify(title, icon, variant) {
                this.$toast({
                    component: ToastificationContent,
                    props: { title, icon, variant }
                });
            }
        }
    }

</script>
